import type { ChartConfig } from '@indutrust/chart-model';
import type { EChartsOption, GridComponentOption } from 'echarts';
import type { EchartsThemeTokens } from './resolve-echarts-theme-tokens';
import { resolveEchartsThemeTokens } from './resolve-echarts-theme-tokens';

/**
 * Optional polish / theme overrides for chart option builders.
 *
 * @example
 * createLineChartOptions(data, config, { theme: resolveEchartsThemeTokens() });
 */
export type ChartOptionBuildOptions = {
  /** Pre-resolved theme tokens; defaults to a fresh {@link resolveEchartsThemeTokens} call. */
  theme?: EchartsThemeTokens;
  /**
   * When true, attaches an ECharts toolbox with `saveAsImage` + `restore`.
   * Intended for the admin Chart Gallery — keep off on production dashboard cards.
   */
  showToolbox?: boolean;
};

/**
 * Default cartesian grid insets that reserve room for axis titles without
 * letting ECharts re-measure labels into the box.
 *
 * ECharts 6 enables `outerBounds` by default (`auto`), which shrinks the plot
 * whenever labels would overflow. Inside fixed-pixel dashboard cards that
 * feedback loop can peg the main thread. Pin insets and set
 * `outerBoundsMode: 'none'` so layout is constant for a given option bag.
 */
export const STABLE_CARTESIAN_GRID: GridComponentOption = {
  containLabel: false,
  outerBoundsMode: 'none',
  left: 48,
  right: 16,
  top: 40,
  bottom: 56,
};

/**
 * Builds a grid option that will not re-enter ECharts' outerBounds / legacy
 * containLabel layout. Spread overrides for chart-specific insets.
 */
export function buildStableGrid(
  overrides: GridComponentOption = {}
): GridComponentOption {
  return {
    ...STABLE_CARTESIAN_GRID,
    ...overrides,
    // Always win — callers must not re-enable dynamic label containment.
    containLabel: false,
    outerBoundsMode: 'none',
  };
}

type ChartTooltipStyle = {
  backgroundColor: string;
  borderColor: string;
  textStyle: { color: string };
};

/**
 * Axis styling shared by cartesian and scatter builders.
 */
export function buildAxisCommon(theme: EchartsThemeTokens) {
  return {
    axisLabel: { color: theme.mutedColor },
    axisLine: { lineStyle: { color: theme.borderColor } },
    nameTextStyle: { color: theme.mutedColor },
    splitLine: {
      lineStyle: { color: theme.splitLineColor, type: 'dashed' as const },
    },
  };
}

/**
 * Picks a color from the palette for the Nth dataset / slice, cycling if needed.
 */
export function pickColor(palette: readonly string[], index: number): string {
  return palette[index % palette.length];
}

/**
 * Returns the chart's color palette, falling back to the theme palette.
 */
export function resolvePalette(
  config: ChartConfig,
  theme: EchartsThemeTokens
): readonly string[] {
  return config.colors ?? theme.palette;
}

export function buildTextStyle(theme: EchartsThemeTokens): { color: string } {
  return { color: theme.textColor };
}

export function buildTooltipStyle(
  theme: EchartsThemeTokens
): ChartTooltipStyle {
  return {
    backgroundColor: theme.tooltipBackground,
    borderColor: theme.tooltipBorder,
    textStyle: { color: theme.textColor },
  };
}

export function buildSharedChrome(
  theme: EchartsThemeTokens,
  showToolbox: boolean
): Partial<EChartsOption> {
  const chrome: Partial<EChartsOption> = {
    color: [...theme.palette],
    textStyle: buildTextStyle(theme),
    animationDuration: 600,
    animationEasing: 'cubicOut',
    tooltip: buildTooltipStyle(theme),
    legend: {
      textStyle: { color: theme.mutedColor },
    },
  };

  if (showToolbox) {
    chrome.toolbox = {
      feature: {
        saveAsImage: { title: 'Save' },
        restore: { title: 'Restore' },
      },
      iconStyle: { borderColor: theme.mutedColor },
    };
  }

  return chrome;
}

/**
 * Adds an alpha channel to a hex or `rgb()` color, passing anything else
 * through untouched so an already-transparent value is not mangled.
 */
export function hexToRgba(color: string, alpha: number): string {
  const trimmed = color.trim();
  if (trimmed.startsWith('#')) {
    const hex = trimmed.slice(1);
    const normalized =
      hex.length === 3
        ? hex
            .split('')
            .map((c) => `${c}${c}`)
            .join('')
        : hex;
    if (normalized.length === 6) {
      const r = Number.parseInt(normalized.slice(0, 2), 16);
      const g = Number.parseInt(normalized.slice(2, 4), 16);
      const b = Number.parseInt(normalized.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }
  if (trimmed.startsWith('rgb(')) {
    return trimmed.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
  }
  return trimmed;
}
